import React from "react";
import { Box, CircularProgress } from "@mui/joy";
import { usePermissions, Permission } from "../../contexts/app/permissions";
import DarkCard from "../common/DarkCard";
import DarkTypography from "../common/DarkTypography";
import { NO_ACCESS_MESSAGE } from "../common/DashboardPage";
import { colorTokens } from "../../styles/globalStyles";
import { useStatsPeriod } from "./hooks/useStatsPeriod";
import { usePeriodStatsData } from "./hooks/usePeriodStatsData";
import StatsPeriodToolbar from "./display/StatsPeriodToolbar";
import StatsSection from "./display/StatsSection";
import StatsTemplateCard from "./display/StatsTemplateCard";
import StatsMetricCard from "./display/StatsMetricCard";
import StatsMonthlyChart from "./display/StatsMonthlyChart";
import InstructorStatsSection from "./display/InstructorStatsSection";
import {
  STATS_METRIC_GROUPS,
  STATS_MONTHLY_CHARTS,
  STATS_TEMPLATE_TABLES,
  buildMonthRows,
} from "./statsPeriodHelpers";

const fullWidthSx = { gridColumn: "1 / -1", minWidth: 0 };

export default function StatsPage() {
  const { hasPermission } = usePermissions();
  const hasModuleAccess = hasPermission(Permission.STATS_ACCESS);
  const canViewGeneral = hasModuleAccess && hasPermission(Permission.STATS_GENERAL_VIEW);
  const canViewInstructors = hasModuleAccess && hasPermission(Permission.STATS_INSTRUCTORS_VIEW);

  const period = useStatsPeriod();
  const { summary, breakdown, loading } = usePeriodStatsData(
    period.granularity,
    period.range,
    canViewGeneral,
  );

  const monthRows = React.useMemo(
    () =>
      period.granularity === "year" && breakdown.length > 0
        ? buildMonthRows(breakdown, period.bounds.start)
        : null,
    [breakdown, period.bounds.start, period.granularity],
  );

  if (!canViewGeneral && !canViewInstructors) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          minHeight: "50vh",
          p: 3,
        }}
      >
        <DarkTypography
          level="body-lg"
          colorVariant="muted"
          sx={{ textAlign: "center", maxWidth: 400 }}
        >
          {NO_ACCESS_MESSAGE}
        </DarkTypography>
      </Box>
    );
  }

  const stats = canViewGeneral && !loading ? summary : null;

  return (
    <Box
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        display: "flex",
        flexDirection: "column",
        bgcolor: colorTokens.bg.app,
        p: { xs: 1.5, md: 2 },
        pb: { xs: 3, md: 4 },
      }}
    >
      <StatsPeriodToolbar period={period} />

      {canViewGeneral && loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
          <CircularProgress color="neutral" />
          <DarkTypography level="body-sm" colorVariant="muted" sx={{ ml: 2 }}>
            Chargement...
          </DarkTypography>
        </Box>
      )}

      {canViewGeneral && !loading && !summary && (
        <DarkCard sx={{ mb: 2, textAlign: "center" }}>
          <DarkTypography colorVariant="muted">Aucune donnée pour {period.label}.</DarkTypography>
        </DarkCard>
      )}

      {stats && (
        <StatsSection title="Activité">
          <StatsTemplateCard config={STATS_TEMPLATE_TABLES.excursions} stats={stats} />
          <StatsTemplateCard config={STATS_TEMPLATE_TABLES.groups} stats={stats} />
          {monthRows && (
            <Box sx={fullWidthSx}>
              <StatsMonthlyChart config={STATS_MONTHLY_CHARTS.excursions} data={monthRows} />
            </Box>
          )}
        </StatsSection>
      )}

      {(stats || canViewInstructors) && (
        <StatsSection title="Participants">
          {stats && <StatsMetricCard group={STATS_METRIC_GROUPS.instructors} stats={stats} />}
          {stats && <StatsMetricCard group={STATS_METRIC_GROUPS.clients} stats={stats} />}
          {stats && monthRows && (
            <Box sx={fullWidthSx}>
              <StatsMonthlyChart config={STATS_MONTHLY_CHARTS.clients} data={monthRows} />
            </Box>
          )}
          {canViewInstructors && (
            <InstructorStatsSection range={period.range} periodLabel={period.label} />
          )}
        </StatsSection>
      )}

      {stats && (
        <StatsSection title="Formations">
          <Box sx={monthRows ? { minWidth: 0 } : fullWidthSx}>
            <StatsTemplateCard config={STATS_TEMPLATE_TABLES.trainings} stats={stats} />
          </Box>
          {monthRows && (
            <StatsMonthlyChart config={STATS_MONTHLY_CHARTS.trainings} data={monthRows} />
          )}
        </StatsSection>
      )}
    </Box>
  );
}
